import { useState, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import Header from '../components/layout/Header';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  Upload, FileSpreadsheet, X, CheckCircle2, AlertCircle,
  ArrowRight, RefreshCw, ShieldCheck,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const fmt = (v) => {
  if (v === null || v === undefined) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(v);
};

const SectionTable = ({ title, data }) => {
  const segments = Object.keys(data?.segments || {});
  if (segments.length === 0) return null;
  return (
    <Card className="mb-4">
      <CardHeader className="py-3">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold sticky left-0 bg-slate-50 z-10 min-w-[180px]">
                Segment
              </TableHead>
              {MONTHS.map((m) => (
                <TableHead key={m} className="text-right font-semibold text-xs">
                  {m}
                </TableHead>
              ))}
              <TableHead className="text-right font-semibold bg-blue-50">Annual</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {segments.map((seg) => {
              const sv = data.segments[seg];
              return (
                <TableRow key={seg} data-testid={`plan-row-${seg.replace(/[^a-zA-Z]/g, '')}`}>
                  <TableCell className="font-medium sticky left-0 bg-white z-10">
                    {seg}
                  </TableCell>
                  {sv.monthly.map((v, i) => (
                    <TableCell
                      key={i}
                      className="text-right tabular-nums text-xs whitespace-nowrap"
                    >
                      {v ? fmt(v) : '—'}
                    </TableCell>
                  ))}
                  <TableCell className="text-right tabular-nums font-semibold bg-blue-50">
                    {fmt(sv.annual)}
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow className="bg-slate-100 font-bold">
              <TableCell className="sticky left-0 bg-slate-100 z-10">Total</TableCell>
              {(data?.totals?.monthly || []).map((v, i) => (
                <TableCell key={`t-${i}`} className="text-right tabular-nums text-xs whitespace-nowrap">
                  {fmt(v)}
                </TableCell>
              ))}
              <TableCell className="text-right tabular-nums bg-blue-100">
                {fmt(data?.totals?.annual)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

const FunctionPanel = ({ fnKey, fdata }) => {
  return (
    <div data-testid={`plan-panel-${fnKey}`}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
            New Business
          </p>
          <p className="text-2xl font-bold text-slate-900 mt-1">
            {fmt(fdata.new_business.totals.annual)}
          </p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
            Renewal
          </p>
          <p className="text-2xl font-bold text-slate-900 mt-1">
            {fmt(fdata.renewal.totals.annual)}
          </p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-xs uppercase tracking-wide text-blue-700 font-semibold">
            Written Premium (Total GWP)
          </p>
          <p className="text-2xl font-bold text-blue-900 mt-1">
            {fmt(fdata.written_premium_annual)}
          </p>
        </div>
      </div>

      <SectionTable title="New Business Premium — Monthly Plan" data={fdata.new_business} />
      <SectionTable title="Renewal Premium — Monthly Plan" data={fdata.renewal} />
    </div>
  );
};

const PlanUpload = () => {
  const { isAdmin, isELT } = useAuth();
  const canEdit = isAdmin() || isELT();
  const [year, setYear] = useState(2026);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(xlsx|xls)$/i.test(f.name)) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }
    setFile(f);
    setPreview(null);
  };

  const handlePreview = async () => {
    if (!file) {
      toast.error('Please choose an Excel file first.');
      return;
    }
    setParsing(true);
    setPreview(null);
    try {
      const formData = new FormData();
      formData.append('year', year);
      formData.append('plan_file', file);
      const res = await axios.post(`${API}/plan-upload/preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      toast.success('Preview ready — review the plan numbers below.');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to parse file';
      toast.error(typeof msg === 'string' ? msg : 'Failed to parse file');
    } finally {
      setParsing(false);
    }
  };

  const handleCommit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const res = await axios.post(`${API}/plan-upload/commit`, {
        year: preview.year,
        functions: preview.functions,
      });
      const stats = res.data.stats || {};
      toast.success(
        `Plan committed for ${year}. ${stats.plan_updated + stats.created} plan values written · ${stats.actuals_preserved} existing actuals preserved.`,
        { duration: 6000 }
      );
      setFile(null);
      setPreview(null);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to commit plan';
      toast.error(typeof msg === 'string' ? msg : 'Failed to commit plan');
    } finally {
      setCommitting(false);
    }
  };

  if (!canEdit) {
    return (
      <Layout>
        <Header title="Annual Plan Upload" />
        <div className="p-6">
          <Card>
            <CardContent className="p-6 text-center text-slate-600">
              You don&apos;t have permission to upload plan data. Please contact your admin.
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const functionKeys = preview ? Object.keys(preview.functions || {}) : [];

  return (
    <Layout>
      <Header title="Annual Plan Upload" />
      <div className="p-6 max-w-[1400px] mx-auto space-y-6">
        {/* Intro */}
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">
            Annual Plan Upload — Brokerage & MGA
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Upload the yearly &ldquo;MGA and Brokerage Monthly Forecast&rdquo; workbook. Pulse will parse
            <strong> New Business Premium</strong> and <strong>Renewal Premium</strong> by segment
            for every month, then update the <strong>plan values</strong>.
          </p>
          <div className="mt-3 flex items-center gap-2 text-sm bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 max-w-3xl">
            <ShieldCheck className="w-4 h-4 text-emerald-700 flex-shrink-0" />
            <span className="text-emerald-900">
              <strong>Your existing actuals will NOT be touched.</strong> Only the{' '}
              <code className="text-xs bg-emerald-100 px-1 rounded">plan_value</code> field on each
              month is updated. Months you&apos;ve already reported actuals for stay exactly as-is.
            </span>
          </div>
        </div>

        {/* Upload card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Upload Plan File</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-4">
              <div>
                <Label htmlFor="plan-year" className="text-xs text-slate-600">
                  Plan Year
                </Label>
                <Input
                  id="plan-year"
                  type="number"
                  value={year}
                  onChange={(e) => {
                    setYear(parseInt(e.target.value, 10));
                    setPreview(null);
                  }}
                  data-testid="plan-upload-year"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Excel File (.xlsx)</Label>
                {file ? (
                  <div className="flex items-center gap-2 text-sm bg-blue-50 border border-blue-200 rounded px-3 py-2.5 mt-0.5">
                    <FileSpreadsheet className="w-4 h-4 text-blue-700" />
                    <span className="text-slate-700 truncate flex-1">{file.name}</span>
                    <span className="text-xs text-slate-500">
                      {(file.size / 1024).toFixed(1)} KB
                    </span>
                    <button
                      onClick={() => {
                        setFile(null);
                        setPreview(null);
                      }}
                      data-testid="plan-upload-clear"
                      className="p-1 rounded hover:bg-blue-100"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="plan-file-input"
                    className="flex items-center justify-center gap-2 text-sm text-blue-600 hover:text-blue-700 cursor-pointer bg-white border border-slate-300 rounded px-3 py-2.5 hover:border-blue-400 transition-all mt-0.5"
                  >
                    <Upload className="w-4 h-4" />
                    <span>Click to choose Excel file</span>
                    <input
                      id="plan-file-input"
                      type="file"
                      accept=".xlsx,.xls"
                      className="hidden"
                      onChange={handleFileChange}
                      data-testid="plan-upload-input"
                    />
                  </label>
                )}
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <Button
                onClick={handlePreview}
                disabled={parsing || !file}
                data-testid="plan-upload-preview-btn"
                className="bg-slate-900 hover:bg-slate-800 text-white"
              >
                {parsing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Parsing...
                  </>
                ) : (
                  <>
                    Preview Plan <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
              {preview && (
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                  {functionKeys.length} function(s) parsed
                </Badge>
              )}
              {preview?.parse_errors?.length > 0 && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  {preview.parse_errors.length} warning(s)
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        {preview && functionKeys.length > 0 && (
          <>
            <Card className="border-blue-200">
              <CardHeader className="bg-blue-50/40 border-b">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  Preview — Plan Values by Function
                </CardTitle>
              </CardHeader>
              <CardContent className="p-5">
                <Tabs defaultValue={functionKeys[0]} data-testid="plan-preview-tabs">
                  <TabsList>
                    {functionKeys.map((k) => (
                      <TabsTrigger
                        key={k}
                        value={k}
                        className="capitalize"
                        data-testid={`plan-tab-${k}`}
                      >
                        {k}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {functionKeys.map((k) => (
                    <TabsContent key={k} value={k} className="mt-4">
                      <FunctionPanel fnKey={k} fdata={preview.functions[k]} />
                    </TabsContent>
                  ))}
                </Tabs>
              </CardContent>
            </Card>

            <Card className="border-emerald-200 bg-emerald-50/30">
              <CardContent className="p-5 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[300px]">
                  <p className="font-semibold text-slate-900">Ready to commit?</p>
                  <p className="text-sm text-slate-600 mt-1">
                    Clicking <strong>Commit Plan</strong> updates the monthly{' '}
                    <code className="text-xs bg-emerald-100 px-1 rounded">plan_value</code> for
                    every segment × month shown above. <strong>Actuals stay untouched.</strong>
                    New KPIs & segments (Health Benefits for Brokerage, MGA function with Wholesale
                    + Monarch) will be auto-created if missing.
                  </p>
                </div>
                <Button
                  onClick={handleCommit}
                  disabled={committing}
                  data-testid="plan-upload-commit-btn"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  size="lg"
                >
                  {committing ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Committing...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" /> Commit Plan
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default PlanUpload;
